import { useGlobalContext } from 'GlobalContext';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createReseller } from 'services/reseller';
import { searchDocument } from 'services/minhaReceita';
import { searchViaCep } from 'services/zipCode';
import { validateCNPJ, validateCPF } from 'shared/helpers/document';
import { onlyDocumentChars, onlyNumbers } from 'shared/helpers/string';
import validateCustomerForm, {
  IErrors,
} from '../CreateCustomerModal/validations';
import CreateResellerModal from './CreateResellerModal';

interface CreateResellerContextData {
  step: number;
  setStep(n: number): void;
  nextStep(): void;
  previousStep(): void;
  loading: boolean;

  cnpj: string;
  name: string;
  contact_email: string;
  trade_name: string;
  state_registration: string;
  city_registration: string;
  postal_code: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  telephone: string;
  setCnpj(s: string): void;
  setName(s: string): void;
  setContactEmail(s: string): void;
  setTradeName(s: string): void;
  setPostalCode(s: string): void;
  setAddress(s: string): void;
  setNumber(s: string): void;
  setComplement(s: string): void;
  setNeighborhood(s: string): void;
  setPhone(s: string): void;
  invalidCep: boolean;
  errors: IErrors;

  servicePrice: number;
  setServicePrice(n: number): void;

  validateCustomerStep(): boolean;
  handleSubmit(): void;
}

const Context = createContext<CreateResellerContextData>(
  {} as CreateResellerContextData,
);

export const useCreateResellerModalContext = () => useContext(Context);

const RESELLER_DEFAULT_PRICE = 170;

const CreateResellerContext = () => {
  const navigate = useNavigate();
  const { confirm, createResellerModalOpen, setCreateResellerModalOpen } =
    useGlobalContext();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [cnpj, setCnpj] = useState('');
  const [name, setName] = useState('');
  const [contact_email, setContactEmail] = useState('');
  const [trade_name, setTradeName] = useState('');
  const [state_registration, setState] = useState('');
  const [city_registration, setCity] = useState('');
  const [postal_code, setPostalCode] = useState('');
  const [logradouro, setAddress] = useState('');
  const [numero, setNumber] = useState('');
  const [complemento, setComplement] = useState('');
  const [bairro, setNeighborhood] = useState('');
  const [telephone, setPhone] = useState('');
  const [invalidCep, setInvalidCep] = useState(false);
  const [errors, setErrors] = useState<IErrors>({});

  const [servicePrice, setServicePrice] = useState(RESELLER_DEFAULT_PRICE);

  const nextStep = () => setStep((s) => s + 1);
  const previousStep = () => setStep((s) => s - 1);

  const onClose = () => setCreateResellerModalOpen(false);

  const searchZipCode = useCallback(() => {
    if (!postal_code || postal_code.length < 9) {
      setInvalidCep(false);
      return;
    }

    if (postal_code.length === 9) {
      searchViaCep(postal_code)
        .then(({ data }) => {
          if (data.erro) {
            setInvalidCep(true);
            setCity('');
            setState('');
            return;
          }
          setInvalidCep(false);
          setAddress(data.logradouro);
          setNeighborhood(data.bairro);
          setCity(data.localidade);
          setState(data.uf);
        })
        .catch(() => {
          setInvalidCep(true);
          setCity('');
          setState('');
        });
    }
  }, [postal_code]);

  const searchMinhaReceitaInfo = useCallback(() => {
    if (!cnpj) return;
    if (
      (validateCPF(onlyDocumentChars(cnpj)) ||
        validateCNPJ(onlyDocumentChars(cnpj))) &&
      cnpj.length >= 14
    ) {
      searchDocument(cnpj)
        .then(({ data }) => {
          if (data.razao_social) setTradeName(data.razao_social);
          if (data.nome_fantasia) setName(data.nome_fantasia);
          if (data.email) setContactEmail(data.email);
          if (data.ddd_telefone_1) setPhone(data.ddd_telefone_1);
          if (data.cep)
            setPostalCode(`${data.cep.slice(0, 5)}-${data.cep.slice(-3)}`);
          if (data.logradouro)
            setAddress(
              `${data.descricao_tipo_de_logradouro} ${data.logradouro}`,
            );
          if (data.numero) setNumber(data.numero);
          if (data.complemento) setComplement(data.complemento);
          if (data.bairro) setNeighborhood(data.bairro);
          if (data.municipio) setCity(data.municipio);
          if (data.uf) setState(data.uf);
        })
        .catch(() =>
          console.error(
            `Could not load data from MinhaReceita for document ${cnpj}`,
          ),
        );
    }
  }, [cnpj]);

  useEffect(() => {
    searchZipCode();
  }, [searchZipCode]);

  useEffect(() => {
    searchMinhaReceitaInfo();
  }, [searchMinhaReceitaInfo]);

  useEffect(() => {
    if (!createResellerModalOpen) {
      setStep(0);
      setCnpj('');
      setName('');
      setContactEmail('');
      setTradeName('');
      setState('');
      setCity('');
      setPostalCode('');
      setAddress('');
      setNumber('');
      setComplement('');
      setNeighborhood('');
      setPhone('');
      setInvalidCep(false);
      setErrors({});
      setServicePrice(RESELLER_DEFAULT_PRICE);
      setLoading(false);
    }
  }, [createResellerModalOpen]);

  const validateCustomerStep = (): boolean => {
    const err = validateCustomerForm({
      cnpj: onlyDocumentChars(cnpj),
      name: name.trim(),
      contact_email: contact_email.trim(),
      trade_name: trade_name.trim(),
      state_registration: state_registration.trim(),
      city_registration: city_registration.trim(),
      postal_code: postal_code.trim(),
      logradouro: logradouro.trim(),
      numero: numero.trim(),
      bairro: bairro.trim(),
      telephone: onlyNumbers(telephone),
    });

    setErrors(err);
    return Object.values(err).length === 0;
  };

  const handleSubmit = () => {
    if (loading) return;

    const payload = {
      customer: {
        cnpj: onlyDocumentChars(cnpj),
        name: name.trim(),
        contact_email: contact_email.trim(),
        trade_name: trade_name.trim(),
        state_registration: state_registration.trim(),
        city_registration: city_registration.trim(),
        postal_code: postal_code.trim(),
        logradouro: logradouro.trim(),
        numero: numero.trim(),
        complemento: complemento.trim(),
        bairro: bairro.trim(),
        telephone: onlyNumbers(telephone),
      },
      service_price: servicePrice,
    };

    confirm('Cadastrar novo revendedor', 'Deseja continuar?', () => {
      setLoading(true);
      createReseller(payload)
        .then(() => {
          toast.success('Novo revendedor cadastrado com sucesso');
          navigate(`customer/${onlyDocumentChars(cnpj)}`);
          onClose();
        })
        .catch(() => toast.error('Falha ao cadastrar novo revendedor'))
        .finally(() => setLoading(false));
    });
  };

  const data: CreateResellerContextData = {
    step,
    setStep,
    nextStep,
    previousStep,
    loading,
    cnpj,
    name,
    contact_email,
    trade_name,
    state_registration,
    city_registration,
    postal_code,
    logradouro,
    numero,
    complemento,
    bairro,
    telephone,
    setCnpj,
    setName,
    setContactEmail,
    setTradeName,
    setPostalCode,
    setAddress,
    setNumber,
    setComplement,
    setNeighborhood,
    setPhone,
    invalidCep,
    errors,
    servicePrice,
    setServicePrice,
    validateCustomerStep,
    handleSubmit,
  };

  return (
    <Context.Provider value={data}>
      <CreateResellerModal isOpen={createResellerModalOpen} onClose={onClose} />
    </Context.Provider>
  );
};

export default CreateResellerContext;
